import type * as Y from "yjs"
import {
  VocabularyEntryOutcomeEnum,
  VocabularyEntryStateEnum,
  type VocabularyCorrectionKindEnum,
  type VocabularyEntryCreatedByRoleEnum,
  type VocabularyEntryFailureCodeEnum,
} from "@/__generated__/graphql"
import type { FeedVocabularyEntry, VocabularyEntryUpdate } from "@/lib/vocabulary-entries"

// The "Vocabulary this lesson" strip is backed by an APPEND-ONLY event log on
// the shared Y.Doc (#825 phase 2). Two clients append concurrently (the
// capturer on capture, whichever client receives a subscription event, both on
// hydration) and Yjs merges appends without conflict; the rows the strip shows
// are a reduction — the latest event per entry, cancelled ones dropped. Nothing
// is ever edited in place.
export const CAPTURES_ARRAY = "captures"

export interface CaptureEvent {
  entryId: string
  state: VocabularyEntryStateEnum
  outcome: VocabularyEntryOutcomeEnum
  failureCode: VocabularyEntryFailureCodeEnum | null
  createdByRole: VocabularyEntryCreatedByRoleEnum
  text: string
  // #825 phase 3. `text` stays what was captured (the mark in the notes is on
  // that text); `correctedText` is what the card was built on, null when
  // nothing was corrected. Events written before phase 3 have neither key —
  // normalizeCaptureEvent fills them in on the way out.
  correctedText: string | null
  correctionKind: VocabularyCorrectionKindEnum | null
  // The server's "a revert is under way or done". Both of canKeepSpelling's
  // parameters are optional, so an event without this key type-checks and reads
  // as unclaimed — the strip would go on offering the action for the whole
  // rebuild and a tap would be answered in_progress. Carried here rather than
  // derived, because it is server truth like the other two.
  skipCorrection: boolean
  flashcardIds: string[]
  scheduledAt: string
  // Server clock — the ordering key. Never compared against Date.now().
  updatedAt: string
  createdAt: string
}

// The reduced, latest-per-entry view the strip renders.
export type CaptureRow = CaptureEvent

// Ties on updatedAt (same second) resolve by lifecycle order; every terminal
// state ranks the same, and the log position breaks the remaining tie.
const STATE_RANK: Record<VocabularyEntryStateEnum, number> = {
  [VocabularyEntryStateEnum.Pending]: 0,
  [VocabularyEntryStateEnum.Processing]: 1,
  [VocabularyEntryStateEnum.Completed]: 2,
  [VocabularyEntryStateEnum.Failed]: 2,
  [VocabularyEntryStateEnum.Cancelled]: 2,
}

// The state-level display outcome, mirroring VocabularyEntry::OUTCOMES_BY_STATE.
// Only the TipTap mark's hover label reads it (a mark carries `state`, not
// `outcome`); every other surface reads `outcome` straight off the row.
export const OUTCOME_FOR_STATE: Record<VocabularyEntryStateEnum, VocabularyEntryOutcomeEnum> = {
  [VocabularyEntryStateEnum.Pending]: VocabularyEntryOutcomeEnum.Queued,
  [VocabularyEntryStateEnum.Processing]: VocabularyEntryOutcomeEnum.Creating,
  [VocabularyEntryStateEnum.Completed]: VocabularyEntryOutcomeEnum.Created,
  [VocabularyEntryStateEnum.Failed]: VocabularyEntryOutcomeEnum.Failed,
  [VocabularyEntryStateEnum.Cancelled]: VocabularyEntryOutcomeEnum.Cancelled,
}

// An event as it may actually sit in the log: a peer running a bundle that
// predates a field wrote it without those keys, and Yjs relays what it does not
// know about untouched. This is the input side of the append-only contract —
// CaptureEvent is what a reader gets back, after normalizeCaptureEvent.
export type StoredCaptureEvent = Omit<CaptureEvent, "correctedText" | "correctionKind" | "skipCorrection"> &
  Partial<Pick<CaptureEvent, "correctedText" | "correctionKind" | "skipCorrection">>

// Every read funnels through latestCaptureEvents, so normalizing there is
// enough; it is exported for any direct reader of the Y.Array.
export function normalizeCaptureEvent(event: StoredCaptureEvent): CaptureEvent {
  return {
    ...event,
    correctedText: event.correctedText ?? null,
    correctionKind: event.correctionKind ?? null,
    // An older peer's event has no claim to report; false is what canKeepSpelling
    // already reads `undefined` as, said out loud so no reader has to know that.
    skipCorrection: event.skipCorrection ?? false,
  }
}

export function captureEventFrom(entry: VocabularyEntryUpdate | FeedVocabularyEntry): CaptureEvent {
  return {
    entryId: entry.id,
    state: entry.state,
    outcome: entry.outcome,
    failureCode: entry.failureCode ?? null,
    createdByRole: entry.createdByRole,
    text: entry.sourceText,
    correctedText: entry.correctedText ?? null,
    correctionKind: entry.correctionKind ?? null,
    skipCorrection: entry.skipCorrection ?? false,
    flashcardIds: [...entry.flashcardIds],
    scheduledAt: entry.scheduledAt,
    updatedAt: entry.updatedAt,
    createdAt: entry.createdAt,
  }
}

// Latest event per entry, cancelled included (the no-op rule needs to see a
// cancel). Order: updatedAt asc, then STATE_RANK, then log position — so the
// last one written for an entry wins. Stored shape in, complete shape out:
// normalizeCaptureEvent here is the only crossing, which is why the rows a
// caller gets never carry undefined where it expects `string | null`.
export function latestCaptureEvents(events: StoredCaptureEvent[]): Map<string, CaptureEvent> {
  const ordered = events
    .map((raw, index) => ({ event: normalizeCaptureEvent(raw), index }))
    .sort(
      (a, b) =>
        Date.parse(a.event.updatedAt) - Date.parse(b.event.updatedAt) ||
        STATE_RANK[a.event.state] - STATE_RANK[b.event.state] ||
        a.index - b.index,
    )
  const latest = new Map<string, CaptureEvent>()
  for (const { event } of ordered) latest.set(event.entryId, event)
  return latest
}

export function reduceCaptureEvents(events: StoredCaptureEvent[]): CaptureRow[] {
  return [...latestCaptureEvents(events).values()]
    .filter((row) => row.state !== VocabularyEntryStateEnum.Cancelled)
    // Newest capture first (the strip is a most-recent-first feed).
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
}

// Append unless the event can no longer change the row: strictly older than the
// current event for that entry (it could never win the reduction), or the same
// instant with the same state AND the same correctedText AND the same claim (a
// duplicate delivery). A different state at the same instant (pending →
// processing within a second) is appended and ranked, and so is a revert that
// lands in the same second as the correction it undoes — it keeps the state and
// only changes the text or the claim. Hydration and duplicate deliveries
// therefore cost nothing.
//
// skipCorrection has to be in that comparison, not just correctedText: an
// ACCEPTED revert changes only the claim — the job clears correctedText later,
// from its own broadcast — and the completion and the stamp land within the
// same second on the interleaving hand_off_revert! calls the favoured one.
// Comparing the two fields the event is about, and not the one the surfaces
// gate on, would drop the claim and leave the strip offering a second tap.
export function appendCaptureEvent(doc: Y.Doc, event: CaptureEvent): void {
  const arr = doc.getArray<StoredCaptureEvent>(CAPTURES_ARRAY)
  const current = latestCaptureEvents(arr.toArray()).get(event.entryId)
  if (current) {
    const currentAt = Date.parse(current.updatedAt)
    const eventAt = Date.parse(event.updatedAt)
    if (
      currentAt > eventAt ||
      (currentAt === eventAt &&
        current.state === event.state &&
        current.correctedText === (event.correctedText ?? null) &&
        current.skipCorrection === (event.skipCorrection ?? false))
    ) {
      return
    }
  }
  doc.transact(() => {
    arr.push([event])
  })
}
